import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { TextLink } from '../../../components/TextLink';
import { apiErrorMessageKey } from '../../../lib/api';
import { useRequestPasswordResetMutation } from '../../../lib/auth';
import { isValidEmail } from '../../../lib/validators';

export function ForgotPasswordPage(): ReactElement {
    const { t } = useTranslation();
    const mutation = useRequestPasswordResetMutation();
    const [email, setEmail] = useState('');

    const emailValid = isValidEmail(email);
    const errorMessage = mutation.error
        ? t(apiErrorMessageKey(mutation.error, 'ERRORS.RESET_PASSWORD_FAILED'))
        : undefined;

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!emailValid || mutation.isPending) {
            return;
        }
        mutation.mutate({ email });
    };

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="h5" component="h1">
                    {t('AUTH.FORGOT_PASSWORD.TITLE')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('AUTH.FORGOT_PASSWORD.DESCRIPTION')}
                </Typography>
            </Stack>

            <Stack component="form" spacing={2} noValidate onSubmit={handleSubmit}>
                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                {mutation.isSuccess && <Alert severity="success">{t('AUTH.FORGOT_PASSWORD.LINK_SENT')}</Alert>}
                <TextField
                    label={t('AUTH.LOGIN.FIELDS.EMAIL')}
                    type="email"
                    fullWidth
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <Button type="submit" variant="contained" fullWidth disabled={!emailValid || mutation.isPending}>
                    {t('AUTH.FORGOT_PASSWORD.SUBMIT')}
                </Button>
                <TextLink to="/login" variant="body2" color="primary" sx={{ alignSelf: 'flex-end' }}>
                    {t('AUTH.BACK_TO_SIGN_IN')}
                </TextLink>
            </Stack>
        </Stack>
    );
}
